import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Paper,
  Grid,
  Button,
  TextField,
  FormControlLabel,
  Switch,
  Divider,
  Alert,
  Tabs,
  Tab,
  IconButton,
  Tooltip,
  Snackbar,
} from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { useParams, useNavigate } from "react-router-dom";
import { useUser } from "../contexts/useUser";
import { CustomerUsageWidget } from "./CustomerUsageWidget";
import { fetchCustomerUsageToken } from "../dataaccess/fetchCustomerUsageTokenPublicAPI";
import { API } from "aws-amplify";
import { apiKeyManagementHeaders } from "../utils/apiKeyManagementAuth";

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`embed-tabpanel-${index}`}
      aria-labelledby={`embed-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );
}

export const CustomerUsageWidgetPage: React.FC = () => {
  const { customerId } = useParams<{ customerId: string }>();
  const navigate = useNavigate();
  const { project } = useUser();

  // State for widget configuration
  const [showTierInfo, setShowTierInfo] = useState(true);
  const [showReplenishmentInfo, setShowReplenishmentInfo] = useState(true);
  const [compact, setCompact] = useState(false);
  const [showCallsGrid, setShowCallsGrid] = useState(false);
  const [theme, setTheme] = useState<"light" | "dark" | "auto">("light");
  const [activeTab, setActiveTab] = useState(0);
  const [copySnackbarOpen, setCopySnackbarOpen] = useState(false);
  const [widgetWidth, setWidgetWidth] = useState("800");
  const [widgetHeight, setWidgetHeight] = useState("600");
  const [apiKey1, setApiKey1] = useState("");
  const [apiKey2, setApiKey2] = useState("");
  const [apiKey, setApiKey] = useState("");
  const [apiKeysConfigured, setApiKeysConfigured] = useState(false);
  const [jwtToken, setJwtToken] = useState("");
  const [tokenLoading, setTokenLoading] = useState(false);
  const [tokenError, setTokenError] = useState("");

  useEffect(() => {
    if (project?.id) {
      getAPIKeys();
    }
  }, [project?.id]);

  useEffect(() => {
    // Generate JWT token when we have API keys and customerId
    if (apiKey1 && customerId) {
      generateJWTToken();
    }
  }, [apiKey1, customerId]);

  const getAPIKeys = async () => {
    if (!project?.id) return;

    API.get("eventapi", "/getIntegrationApiKeys", {
      headers: await apiKeyManagementHeaders(),
      queryStringParameters: {
        projectId: project.id,
      },
    })
      .then((response) => {
        if (response.success) {
          const keys = JSON.parse(response.body);
          setApiKeysConfigured(Boolean(keys.key1Configured || keys.key2Configured || keys.key1 || keys.key2));
          setApiKey1(keys.key1 || "");
          setApiKey2(keys.key2 || "");
          setApiKey(keys.key1 || keys.key2 || "");
          if ((keys.key1Configured || keys.key2Configured) && !keys.key1 && !keys.key2) {
            setTokenError("Plaintext keys are not stored. Paste an integration API key below, then retry token generation.");
          }
        }
      })
      .catch((error) => {
        console.error(error);
      });
  };

  const generateJWTToken = async () => {
    if (!project?.id || !customerId) return;

    setTokenLoading(true);
    setTokenError("");

    try {
      const response = await fetchCustomerUsageToken(
        project.id,
        decodeURIComponent(customerId),
        apiKey1 || apiKey // Use the first available API key
      );
      setJwtToken(response.token);
    } catch (error) {
      console.error("Failed to generate JWT token:", error);
      setTokenError(
        "Failed to generate token. Please ensure you have valid API keys."
      );
    } finally {
      setTokenLoading(false);
    }
  };

  // Get API key - in a real implementation, you'd fetch this from your project
  // For MVP, we'll use a placeholder that the customer would replace
  const projectId = project?.id || "YOUR_PROJECT_ID";

  if (!customerId) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">No customer ID provided in URL</Alert>
      </Box>
    );
  }

  if (!project) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">No project context available</Alert>
      </Box>
    );
  }

  const decodedCustomerId = decodeURIComponent(customerId);

  // Generate embed codes using JWT token - dynamically detect current host
  const getBaseUrl = () => {
    if (typeof window === 'undefined') {
      // Server-side rendering fallback
      return process.env.NODE_ENV === "development"
        ? "http://localhost:3000"
        : "https://app.llmasaservice.io";
    }

    const { protocol, hostname, port } = window.location;
    
    // Handle localhost development
    if (hostname === 'localhost' || hostname === '127.0.0.1') {
      return `${protocol}//${hostname}${port ? `:${port}` : ''}`;
    }
    
    // Handle known production domains
    if (hostname === 'dev.llmasaservice.io' || hostname === 'app.llmasaservice.io') {
      return `${protocol}//${hostname}`;
    }
    
    // Fallback for any other domains (use current host)
    return `${protocol}//${hostname}${port ? `:${port}` : ''}`;
  };

  const baseUrl = getBaseUrl();

  // Remove projectId from customerId before encoding for embed URLs
  const cleanCustomerId = decodeURIComponent(customerId).replace(projectId, "");
  const encodedCleanCustomerId = encodeURIComponent(cleanCustomerId);

  const iframeCode = `<iframe 
  src="${baseUrl}/embed/usage/${encodedCleanCustomerId}?token=${jwtToken}&theme=${theme}&compact=${compact}&showTierInfo=${showTierInfo}&showReplenishmentInfo=${showReplenishmentInfo}&showCallsGrid=${showCallsGrid}"
  width="${widgetWidth}" 
  height="${widgetHeight}"
  frameborder="0"
  style="border: 1px solid #e0e0e0; border-radius: 8px;"
  sandbox="allow-scripts allow-same-origin">
</iframe>`;

  const widgetUrl = `${baseUrl}/embed/usage/${encodedCleanCustomerId}?token=${jwtToken}&theme=${theme}&compact=${compact}&showTierInfo=${showTierInfo}&showReplenishmentInfo=${showReplenishmentInfo}&showCallsGrid=${showCallsGrid}`;

  const tokenApiCode = `// This code MUST be executed server-side to protect your API key
// Example using Node.js/Express

const express = require('express');
const axios = require('axios');
const app = express();

app.use(express.json());

app.post('/api/customer-usage-token', async (req, res) => {
  const { customerId } = req.body;
  const projectId = '${projectId}'; // Your project ID
  const apiKey = 'YOUR_API_KEY_HERE'; // Replace with your actual API key
  
  try {
    // Your customer ID sometimes has the project ID at the beginning
    const cleanCustomerId = customerId.replace(projectId, '');
    const encodedCustomerId = encodeURIComponent(cleanCustomerId);
    
    const response = await axios.get(\`https://api.llmasaservice.io/customers/\${encodedCustomerId}/usageEmbedToken\`, {
      headers: {
        'Authorization': \`Bearer \${apiKey}\`
      }
    });
    
    res.json({ 
      token: response.data.token,
      expiresIn: response.data.expiresIn,
      expiresAt: response.data.expiresAt
    });
  } catch (error) {
    console.error('Token generation failed:', error);
    res.status(500).json({ error: 'Failed to generate token' });
  }
});

// Then use the token in your frontend iframe:
// <iframe src="${baseUrl}/embed/usage/{cleanCustomerId}?token={token_from_your_server}"></iframe>`;

  const handleCopyCode = (code: string) => {
    navigator.clipboard.writeText(code).then(() => {
      setCopySnackbarOpen(true);
    });
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ mb: 3 }}>
        <Button variant="outlined" onClick={() => navigate("/customers")}>
          ← Back to Customers
        </Button>
      </Box>

      <Grid container spacing={3}>
        {/* Widget Preview */}
        <Grid item xs={12} lg={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Widget Preview
            </Typography>

            {/* Configuration Controls */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle2" gutterBottom>
                Configuration
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={compact}
                        onChange={(e) => setCompact(e.target.checked)}
                      />
                    }
                    label="Compact Mode"
                  />
                </Grid>
                <Grid item xs={6}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={showTierInfo}
                        onChange={(e) => setShowTierInfo(e.target.checked)}
                      />
                    }
                    label="Show Tier Info"
                  />
                </Grid>
                <Grid item xs={6}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={showReplenishmentInfo}
                        onChange={(e) =>
                          setShowReplenishmentInfo(e.target.checked)
                        }
                      />
                    }
                    label="Show Replenishment"
                  />
                </Grid>
                <Grid item xs={6}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={showCallsGrid}
                        onChange={(e) => setShowCallsGrid(e.target.checked)}
                      />
                    }
                    label="Show API Calls Grid"
                  />
                </Grid>
                <Grid item xs={4}>
                  <TextField
                    select
                    label="Theme"
                    value={theme}
                    onChange={(e) =>
                      setTheme(e.target.value as "light" | "dark" | "auto")
                    }
                    SelectProps={{
                      native: true,
                    }}
                    size="small"
                    fullWidth
                  >
                    <option value="light">Light</option>
                    <option value="dark">Dark</option>
                    <option value="auto">Auto</option>
                  </TextField>
                </Grid>
                <Grid item xs={4}>
                  <TextField
                    label="Width (px)"
                    value={widgetWidth}
                    onChange={(e) => setWidgetWidth(e.target.value)}
                    size="small"
                    fullWidth
                    type="number"
                  />
                </Grid>
                <Grid item xs={4}>
                  <TextField
                    label="Height (px)"
                    value={widgetHeight}
                    onChange={(e) => setWidgetHeight(e.target.value)}
                    size="small"
                    fullWidth
                    type="number"
                  />
                </Grid>
              </Grid>
            </Box>

            <Divider sx={{ mb: 3 }} />

            {apiKeysConfigured && !apiKey1 && !apiKey2 && (
              <TextField
                fullWidth
                type="password"
                label="Integration API key"
                value={apiKey}
                onChange={(event) => setApiKey(event.target.value)}
                helperText="Used only in this browser session to generate the usage token."
                autoComplete="off"
                sx={{ mb: 3 }}
              />
            )}

            {/* Widget Preview */}
            <Box
              sx={{
                p: 2,
                border: "2px dashed #ccc",
                borderRadius: 2,
                backgroundColor: "#fafafa",
              }}
            >
              <CustomerUsageWidget
                customerId={decodedCustomerId}
                projectId={projectId}
                jwtToken={jwtToken}
                apiKey={apiKey} // Fallback for backward compatibility
                theme={theme}
                compact={compact}
                showTierInfo={showTierInfo}
                showReplenishmentInfo={showReplenishmentInfo}
                showCallsGrid={showCallsGrid}
              />
            </Box>

            {/* Token Status */}
            <Box sx={{ mt: 2 }}>
              {tokenLoading && (
                <Alert severity="info">Generating JWT token...</Alert>
              )}
              {tokenError && (
                <Alert
                  severity="error"
                  action={
                    <Button
                      color="inherit"
                      size="small"
                      onClick={generateJWTToken}
                    >
                      Retry
                    </Button>
                  }
                >
                  {tokenError}
                </Alert>
              )}
              {jwtToken && !tokenLoading && !tokenError && (
                <Alert
                  severity="success"
                  action={
                    <Button
                      color="inherit"
                      size="small"
                      onClick={generateJWTToken}
                    >
                      Refresh Token
                    </Button>
                  }
                >
                  JWT token generated successfully (expires in 1 hour)
                </Alert>
              )}
            </Box>
          </Paper>
        </Grid>

        {/* Embed Code */}
        <Grid item xs={12} lg={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Embed Code
            </Typography>

            <Tabs value={activeTab} onChange={handleTabChange}>
              <Tab label="iframe" />
              <Tab label="Get Token" />
              <Tab label="URL" />
            </Tabs>

            <TabPanel value={activeTab} index={0}>
              <Box sx={{ position: "relative", pr: 8 }}>
                <TextField
                  multiline
                  rows={8}
                  fullWidth
                  value={iframeCode}
                  variant="outlined"
                  InputProps={{
                    readOnly: true,
                    sx: { fontFamily: "monospace", fontSize: "0.875rem" },
                  }}
                />
                <Tooltip title="Copy to clipboard">
                  <IconButton
                    sx={{ 
                      position: "absolute", 
                      top: 8, 
                      right: 16,
                      backgroundColor: "background.paper",
                      boxShadow: 2,
                      border: "1px solid",
                      borderColor: "divider",
                      "&:hover": {
                        backgroundColor: "action.hover",
                        boxShadow: 3,
                      }
                    }}
                    onClick={() => handleCopyCode(iframeCode)}
                  >
                    <ContentCopyIcon />
                  </IconButton>
                </Tooltip>
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Simple iframe embed - works in any HTML page. The iframe will
                automatically adjust to show current usage data.
              </Typography>
            </TabPanel>

            <TabPanel value={activeTab} index={1}>
              <Alert severity="error" sx={{ mb: 2 }}>
                <Typography variant="body2">
                  <strong>SECURITY WARNING:</strong> This code MUST be executed
                  server-side to avoid exposing your API key. Never include your
                  API key in client-side code.
                </Typography>
              </Alert>
              <Box sx={{ position: "relative", pr: 8 }}>
                <TextField
                  multiline
                  rows={15}
                  fullWidth
                  value={tokenApiCode}
                  variant="outlined"
                  InputProps={{
                    readOnly: true,
                    sx: { fontFamily: "monospace", fontSize: "0.875rem" },
                  }}
                />
                <Tooltip title="Copy to clipboard">
                  <IconButton
                    sx={{ 
                      position: "absolute", 
                      top: 8, 
                      right: 16,
                      backgroundColor: "background.paper",
                      boxShadow: 2,
                      border: "1px solid",
                      borderColor: "divider",
                      "&:hover": {
                        backgroundColor: "action.hover",
                        boxShadow: 3,
                      }
                    }}
                    onClick={() => handleCopyCode(tokenApiCode)}
                  >
                    <ContentCopyIcon />
                  </IconButton>
                </Tooltip>
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Server-side implementation for securely generating JWT tokens.
                Create this endpoint on your server and call it from your
                frontend to get tokens.
              </Typography>
            </TabPanel>

            <TabPanel value={activeTab} index={2}>
              <Box sx={{ position: "relative", pr: 8 }}>
                <TextField
                  multiline
                  rows={3}
                  fullWidth
                  value={widgetUrl}
                  variant="outlined"
                  InputProps={{
                    readOnly: true,
                    sx: { fontFamily: "monospace", fontSize: "0.875rem" },
                  }}
                />
                <Tooltip title="Copy to clipboard">
                  <IconButton
                    sx={{ 
                      position: "absolute", 
                      top: 8, 
                      right: 16,
                      backgroundColor: "background.paper",
                      boxShadow: 2,
                      border: "1px solid",
                      borderColor: "divider",
                      "&:hover": {
                        backgroundColor: "action.hover",
                        boxShadow: 3,
                      }
                    }}
                    onClick={() => handleCopyCode(widgetUrl)}
                  >
                    <ContentCopyIcon />
                  </IconButton>
                </Tooltip>
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Direct URL to the usage widget. Can be used in iframes or opened
                directly in a new tab/window.
              </Typography>
            </TabPanel>
          </Paper>

          {/* Integration Notes */}
          <Paper sx={{ p: 3, mt: 2 }}>
            <Typography variant="h6" gutterBottom>
              Integration Guide
            </Typography>
            <Typography variant="body2" paragraph>
              <strong>How to Integrate:</strong> 1) Implement the server-side token endpoint from the "Get Token" tab, 2) Call your endpoint to get a JWT token, 3) Build the widget URL with the token, 4) Embed in an iframe. The token shown here is for testing only - you must generate tokens dynamically on your server in production.
            </Typography>
            <Typography variant="body2" paragraph>
              <strong>Security & Tokens:</strong> JWT tokens expire in 1 hour and are scoped to specific customers. Your API key must never be exposed to client-side code - always generate tokens server-side using the endpoint pattern shown in the "Get Token" tab.
            </Typography>
            <Typography variant="body2" paragraph>
              <strong>Widget Configuration:</strong> Default dimensions are 800×600px (adjustable above). The widget auto-refreshes usage data every minute and uses Material-UI components that respect your theme settings.
            </Typography>
            <Typography variant="body2">
              <strong>Important:</strong> URL-encode customer IDs containing special characters (like email addresses) and ensure your server can handle the token generation endpoint before going live.
            </Typography>
          </Paper>
        </Grid>
      </Grid>

      <Snackbar
        open={copySnackbarOpen}
        autoHideDuration={3000}
        onClose={() => setCopySnackbarOpen(false)}
        message="Code copied to clipboard!"
      />
    </Box>
  );
};
